import type { StorefrontOrder } from '@erp/shared';
import { CheckCircle2, PackageSearch } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useParams } from 'react-router-dom';

import { useStorefront } from '../../app/StorefrontLayout.js';
import { Badge } from '../../components/shadcn/index.js';
import { Button, FormField, Input } from '../../components/ui/index.js';
import { formatMoney, trimMoney } from '../../lib/money.js';
import { publicApi } from '../../lib/publicApi.js';

import { FibPayBox } from './FibPayBox.js';
import { OrderSkeleton } from './StorefrontSkeletons.js';

const STATUS_TONE: Record<string, 'success' | 'secondary' | 'danger' | 'default'> = {
  fulfilled: 'success',
  submitted: 'default',
  draft: 'secondary',
  cancelled: 'danger',
};

export function StorefrontOrderPage(): JSX.Element {
  const { t, i18n } = useTranslation('cms');
  const { slug, site } = useStorefront();
  const { number = '' } = useParams();
  const location = useLocation();
  const placedPhone = (location.state as { phone?: string } | null)?.phone;

  const [phone, setPhone] = useState(placedPhone ?? '');
  const [order, setOrder] = useState<StorefrontOrder | null>(null);
  const [status, setStatus] = useState<'idle' | 'loading' | 'ok' | 'notfound'>(placedPhone ? 'loading' : 'idle');

  const lookup = (lookupPhone: string): void => {
    if (!lookupPhone.trim()) return;
    setStatus('loading');
    void publicApi()
      .get<StorefrontOrder>(`/${slug}/orders/${encodeURIComponent(number)}`, { params: { phone: lookupPhone.trim() } })
      .then((r) => { setOrder(r.data); setStatus('ok'); })
      .catch(() => { setOrder(null); setStatus('notfound'); });
  };

  // Auto-fetch when we arrived straight from checkout (phone carried in state).
  useEffect(() => {
    if (placedPhone) lookup(placedPhone);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placedPhone, number, slug]);

  const money = (v: string, currency: string): string => formatMoney(v, { locale: i18n.language, currency });
  // Delivery is an item-less line — show it as a fee below the products, not as a row.
  const itemLines = order ? order.lines.filter((l) => l.itemId) : [];
  const deliveryLine = order ? (order.lines.find((l) => !l.itemId) ?? null) : null;
  const productsSubtotal = order ? Number(order.subtotal) - (deliveryLine ? Number(deliveryLine.lineTotal) : 0) : 0;

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-6">
      <Helmet><title>{`${t('order.title', { number })} · ${site.storeName}`}</title></Helmet>

      {status === 'ok' && order ? (
        <>
          {placedPhone && (
            <div className="flex items-center gap-3 rounded-2xl border border-success/30 bg-success/10 p-4 text-success">
              <CheckCircle2 className="h-6 w-6 shrink-0" aria-hidden />
              <div>
                <div className="font-semibold">{t('order.placed')}</div>
                <div className="text-sm opacity-90">{t('order.placedHint')}</div>
              </div>
            </div>
          )}

          <div className="flex flex-col gap-1">
            <h1 className="text-2xl font-semibold">{t('order.title', { number: order.orderNumber })}</h1>
            <div className="flex items-center gap-3 text-sm text-fg-muted">
              <span>{order.date}</span>
              <Badge variant={STATUS_TONE[order.status] ?? 'default'}>{t(`order.status.${order.status}`, { defaultValue: order.status })}</Badge>
            </div>
          </div>

          <div className="overflow-hidden rounded-2xl border border-border">
            <table className="w-full text-sm">
              <thead className="bg-bg-subtle text-fg-muted">
                <tr>
                  <th className="px-4 py-2 text-start font-medium">{t('order.item')}</th>
                  <th className="px-4 py-2 text-end font-medium">{t('order.qty')}</th>
                  <th className="px-4 py-2 text-end font-medium">{t('order.price')}</th>
                  <th className="px-4 py-2 text-end font-medium">{t('order.lineTotal')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {itemLines.map((l, i) => (
                  <tr key={i}>
                    <td className="px-4 py-2">{l.description}</td>
                    <td className="px-4 py-2 text-end font-mono tabular-nums">{trimMoney(l.quantity)}</td>
                    <td className="px-4 py-2 text-end font-mono tabular-nums">{money(l.unitPrice, order.currencyCode)}</td>
                    <td className="px-4 py-2 text-end font-mono tabular-nums">{money(l.lineTotal, order.currencyCode)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="ms-auto flex w-full max-w-xs flex-col gap-1.5 text-sm">
            <div className="flex justify-between"><span className="text-fg-muted">{t('order.subtotal')}</span><span className="font-mono tabular-nums">{money(String(productsSubtotal), order.currencyCode)}</span></div>
            {deliveryLine && (
              <div className="flex justify-between"><span className="text-fg-muted">{t('order.delivery', { defaultValue: 'Delivery' })}</span><span className="font-mono tabular-nums">{money(deliveryLine.lineTotal, order.currencyCode)}</span></div>
            )}
            {Number(order.discountTotal) > 0 && (
              <div className="flex justify-between text-success"><span>{t('order.discount')}</span><span className="font-mono tabular-nums">−{money(order.discountTotal, order.currencyCode)}</span></div>
            )}
            {Number(order.taxTotal) > 0 && (
              <div className="flex justify-between"><span className="text-fg-muted">{t('order.tax')}</span><span className="font-mono tabular-nums">{money(order.taxTotal, order.currencyCode)}</span></div>
            )}
            <div className="flex justify-between border-t border-border pt-1.5 text-base font-bold"><span>{t('order.total')}</span><span className="font-mono tabular-nums">{money(order.total, order.currencyCode)}</span></div>
            {Number(order.cashbackRedeemed ?? 0) > 0 && (
              <>
                <div className="flex justify-between text-success"><span>{t('order.cashbackUsed', { defaultValue: 'Paid from balance' })}</span><span className="font-mono tabular-nums">−{money(order.cashbackRedeemed ?? '0', order.currencyCode)}</span></div>
                <div className="flex justify-between text-base font-bold"><span>{t('order.amountDue', { defaultValue: 'Amount due' })}</span><span className="font-mono tabular-nums">{money(String(Number(order.total) - Number(order.cashbackRedeemed ?? 0)), order.currencyCode)}</span></div>
              </>
            )}
          </div>

          {/* Server-confirmed FIB payment → the paid chip, never a second pay
              button; onPaid re-reads the order so the state is durable. */}
          {order.fibPaid ? (
            <div className="flex items-center gap-2 rounded-lg border border-success/40 bg-success/10 p-4 text-success">
              <CheckCircle2 className="h-6 w-6 flex-none" aria-hidden />
              <span className="font-semibold">{t('fib.paid', { defaultValue: 'Paid with FIB — thank you!' })}</span>
            </div>
          ) : site.fibEnabled && order.status !== 'cancelled' ? (
            <FibPayBox slug={slug} orderNumber={order.orderNumber} onPaid={() => lookup(phone || placedPhone || '')} />
          ) : null}

          <Link to={`/s/${slug}/shop`} className="text-sm text-brand hover:underline">{t('store.browse')}</Link>
        </>
      ) : status === 'loading' ? (
        <OrderSkeleton />
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-2">
            <PackageSearch className="h-6 w-6 text-fg-muted" aria-hidden />
            <h1 className="text-2xl font-semibold">{t('order.lookupTitle')}</h1>
          </div>
          <p className="text-sm text-fg-muted">{t('order.lookupHint', { number })}</p>
          {status === 'notfound' && <p className="text-sm text-danger" role="alert">{t('order.notFound')}</p>}
          <form className="flex flex-col gap-3" onSubmit={(e) => { e.preventDefault(); lookup(phone); }}>
            <FormField id="lookup-phone" label={t('order.phone', { defaultValue: 'Phone used at checkout' })} required>
              <Input id="lookup-phone" type="tel" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} autoComplete="tel" />
            </FormField>
            <Button type="submit" disabled={!phone.trim()}>{t('order.lookup')}</Button>
          </form>
        </div>
      )}
    </div>
  );
}
